import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Divider, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { SelectElement, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlFeatureType } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import { ObservationTypeUpdateFormFragment } from "./fragments.generated";

type Props = {
    initialData: ObservationTypeUpdateFormFragment;
    onSubmit: (data: ObservationTypeUpdateFormInputs) => void;
    error?: ApolloError;
};

export default function ObservationTypeUpdateForm({ initialData, onSubmit, error }: Props) {
    const { t } = useTranslation("observationType");

    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<ObservationTypeUpdateFormInputs>({
        defaultValues: {
            name: initialData.name,
            description: initialData.description,
            featureType: initialData.featureType,
            requireMediaItems: initialData.requireMediaItems,
            requirePreciseLocation: initialData.requirePreciseLocation,
            showToClientUser: initialData.showToClientUser,
        },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    const featureTypeOptions = Object.values(GqlFeatureType).map(value => ({
        id: value,
        label: t(`observationTypeUpdateForm.label${value}`),
    }));

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("name")}
                    label={t("observationTypeUpdateForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />

                <TextField
                    {...register("description")}
                    label={t("observationTypeUpdateForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    multiline
                />
                <SelectElement
                    control={control}
                    label={t("observationTypeUpdateForm.labelFeatureType")}
                    rules={{ required: true }}
                    name="featureType"
                    valueKey="id"
                    options={featureTypeOptions}
                />
                <Divider textAlign="center">{t("observationTypeUpdateForm.requiredFieldsDivider")}</Divider>
                <Typography>{t("observationTypeUpdateForm.requiredFieldsInfo")}</Typography>

                <SwitchElement
                    name="requireMediaItems"
                    label={t("observationTypeUpdateForm.labelRequireMedia")}
                    control={control}
                />

                <SwitchElement
                    name="requirePreciseLocation"
                    label={t("observationTypeUpdateForm.labelRequirePreciseLocation")}
                    control={control}
                />

                <SwitchElement
                    name="showToClientUser"
                    label={t("observationTypeUpdateForm.labelShowToClientUser")}
                    control={control}
                />

                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("observationTypeUpdateForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type ObservationTypeUpdateFormInputs = {
    name: string;
    description: string | null | undefined;
    featureType: GqlFeatureType;
    requireMediaItems: boolean;
    requirePreciseLocation: boolean;
    showToClientUser: boolean;
};
